import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons';
import { useFeatureGate } from '@statsig/react-bindings';
import { useAtomValue } from 'jotai';
import { ReactNode } from 'react';

import { DRAWER_TITLE_ID, IconButton, Text, cn } from '@growtherapy/sprout-ui';

import { ClientInformationDrawerHeaderContentWrapper as ClientInformationDrawerHeaderConnected } from '@grow-therapy-team/seedling-components';

import { DrawerHeader } from '../../components/DrawerHeader';
import { shouldUseSeedlingClientInfoDrawer } from '../../statsig/gates';
import { ParticipantStatus } from '../../types';
import { scheduledPatientInformationAtom } from '../schedule-preview/state';
import { useGetPatientByShortId } from '../useGetPatientByShortId';
import { getAppointmentClientInfoHeader, getAppointmentType } from './utils';

type ClientInformationDrawerHeaderProps = {
  drawerTitle: string;
  visitorStatus?: ParticipantStatus;
  drawerTitleCaption?: ReactNode;
  onClickBackButton?: () => void;
  shouldShowNewClientInfoDrawer?: boolean;
};

export function ClientInformationHeaderTitleBlock({
  drawerTitle,
  drawerTitleCaption,
  isScheduledClient,
}: {
  drawerTitle: string;
  drawerTitleCaption: ReactNode;
  isScheduledClient: boolean;
}) {
  return (
    <div className='flex flex-col'>
      <div className='flex flex-grow items-center'>
        <span
          className='fs-exclude'
          data-dd-privacy='mask'
          data-dd-action-name='Client Name'
          data-testid='client-information.header.name'
        >
          <Text
            as='span'
            variant={isScheduledClient ? 'lg' : 'md'}
            className={cn('flex flex-col', 'rebrand:typography-subhead-small', {
              'font-medium': isScheduledClient,
            })}
            id={DRAWER_TITLE_ID}
          >
            {drawerTitle}
          </Text>
        </span>
      </div>
      {drawerTitleCaption && (
        <Text as='span' variant='xs' color='subdued' data-testid='client-information.header.caption'>
          {drawerTitleCaption}
        </Text>
      )}
    </div>
  );
}
export function ClientInformationDrawerHeader({
  drawerTitle,
  drawerTitleCaption,
  visitorStatus,
  onClickBackButton,
  shouldShowNewClientInfoDrawer = false,
}: ClientInformationDrawerHeaderProps) {
  const isScheduledClient = visitorStatus === ParticipantStatus.SCHEDULED;

  return (
    <DrawerHeader>
      {shouldShowNewClientInfoDrawer ? (
        <ClientInformationDrawerHeaderConnected
          drawerTitle={
            <ClientInformationHeaderTitleBlock
              drawerTitle={drawerTitle}
              drawerTitleCaption={drawerTitleCaption}
              isScheduledClient={isScheduledClient}
            />
          }
        />
      ) : (
        <div className='flex items-center' data-testid='client-information.header'>
          {onClickBackButton && (
            <IconButton
              onClick={onClickBackButton}
              className='mr-4 w-8 h-8 flex justify-center items-center'
              iconSize='xs'
              aria-label='Go back'
              data-testid='client-information.button.back'
              iconDefinition={faArrowLeft}
            />
          )}
          <ClientInformationHeaderTitleBlock
            drawerTitle={drawerTitle}
            drawerTitleCaption={drawerTitleCaption}
            isScheduledClient={isScheduledClient}
          />
        </div>
      )}
    </DrawerHeader>
  );
}

export function ClientInformationDrawerHeaderWrapper({
  selectedPatientShortId,
  visitorName,
  ...rest
}: Omit<ClientInformationDrawerHeaderProps, 'drawerTitle'> & {
  selectedPatientShortId: string;
  visitorName?: string;
}) {
  const { data: patientChartData } = useGetPatientByShortId(selectedPatientShortId);
  const scheduledPatientInformation = useAtomValue(scheduledPatientInformationAtom);

  const appointmentType = getAppointmentType(
    scheduledPatientInformation?.appointmentType,
    patientChartData?.patientChart?.lastAppointment,
  );

  const { value: shouldShowNewClientInfoDrawer } = useFeatureGate(shouldUseSeedlingClientInfoDrawer);
  return (
    <ClientInformationDrawerHeader
      {...rest}
      drawerTitle={getAppointmentClientInfoHeader(appointmentType, visitorName)}
      shouldShowNewClientInfoDrawer={shouldShowNewClientInfoDrawer}
    />
  );
}
